import type { Event } from '../types/Event';

export type CategoryId =
  | 'music'
  | 'culture'
  | 'sport'
  | 'food'
  | 'kids'
  | 'motoring'
  | 'film'
  | 'fair'
  | 'education'
  | 'other';

export interface CategoryInfo {
  id: CategoryId;
  label: string;
  color: string;
  icon: string;
}

// Central place for category metadata (label, color, icon) used by the hex
// legend, hex popup breakdowns, and individual event marker icons.
export const CATEGORIES: Record<CategoryId, CategoryInfo> = {
  music: { id: 'music', label: 'Muzyka', color: '#8b5cf6', icon: '🎵' },
  culture: { id: 'culture', label: 'Kultura', color: '#10b981', icon: '🎭' },
  sport: { id: 'sport', label: 'Sport', color: '#22c55e', icon: '⚽' },
  food: { id: 'food', label: 'Gastronomia', color: '#f97316', icon: '🍔' },
  kids: { id: 'kids', label: 'Dla dzieci', color: '#ec4899', icon: '🧒' },
  motoring: { id: 'motoring', label: 'Motoryzacja', color: '#64748b', icon: '🚗' },
  film: { id: 'film', label: 'Film', color: '#ef4444', icon: '🎬' },
  fair: { id: 'fair', label: 'Targi', color: '#eab308', icon: '🎪' },
  education: { id: 'education', label: 'Edukacja', color: '#0ea5e9', icon: '📚' },
  other: { id: 'other', label: 'Inne', color: '#3b82f6', icon: '📍' },
};

export const CATEGORY_ORDER: CategoryId[] = [
  'music',
  'culture',
  'sport',
  'food',
  'kids',
  'motoring',
  'film',
  'fair',
  'education',
  'other',
];

// The backend `Event` model has no dedicated `category` column yet — only
// free-form `tags`, a `title` and a `description`. Until a real category
// field exists, we infer one from keywords found in those fields. If a
// `category` column is added later, replace the body of this function with
// a direct read of `event.category`.
// Order matters: more specific categories (film, motoring, fair) are checked
// before "culture" so they don't get swallowed by its broad keywords (e.g.
// "wystaw"), and "education" is checked last since "warsztat" also appears
// in phrases belonging to other categories (e.g. "warsztaty kulinarne").
const KEYWORD_MAP: Array<[CategoryId, RegExp]> = [
  ['music', /koncert|muzyk|festiwal muzyczn|\bdj\b|zespół|gitar/i],
  ['film', /\bfilm\w*|\bkino\b|seans|premiera filmow/i],
  ['motoring', /motoryzacj|motocykl|zlot samochod|wystawa samochod|tuning|silnik/i],
  ['fair', /\btargi\b|jarmark|kiermasz/i],
  ['culture', /kultur|teatr|wystaw|muzeum|sztuk|literatur|spektakl/i],
  ['sport', /sport|bieg|maraton|mecz|turniej|rower|fitness|siatków|piłk/i],
  ['food', /gastronom|jedzeni|\bfood\b|kulinar|piwo|wino|degustacj|street food/i],
  ['kids', /dzieci|rodzin|maluch|junior/i],
  ['education', /edukacj|wykład|warsztat|szkoleni|konferencj|seminari/i],
];

export function getEventCategory(event: Event): CategoryId {
  const haystack = [event.title, event.description, ...(event.tags ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  for (const [category, pattern] of KEYWORD_MAP) {
    if (pattern.test(haystack)) return category;
  }

  return 'other';
}
